/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRiskExclusionsRequest,
  ListRiskExclusionsSecurity,
} from "../models/operations/listriskexclusions.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListExclusionsQuery,
  prefetchRiskListExclusions,
  queryKeyRiskListExclusions,
  RiskListExclusionsQueryData,
} from "./riskListExclusions.core.js";
export {
  buildRiskListExclusionsQuery,
  prefetchRiskListExclusions,
  queryKeyRiskListExclusions,
  type RiskListExclusionsQueryData,
};

export type RiskListExclusionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRiskExclusions risk
 *
 * @remarks
 * List risk exclusions for the current project. Optionally filter to a single policy.
 */
export function useRiskListExclusions(
  request?: ListRiskExclusionsRequest | undefined,
  security?: ListRiskExclusionsSecurity | undefined,
  options?: QueryHookOptions<
    RiskListExclusionsQueryData,
    RiskListExclusionsQueryError
  >,
): UseQueryResult<RiskListExclusionsQueryData, RiskListExclusionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListExclusionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRiskExclusions risk
 *
 * @remarks
 * List risk exclusions for the current project. Optionally filter to a single policy.
 */
export function useRiskListExclusionsSuspense(
  request?: ListRiskExclusionsRequest | undefined,
  security?: ListRiskExclusionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListExclusionsQueryData,
    RiskListExclusionsQueryError
  >,
): UseSuspenseQueryResult<
  RiskListExclusionsQueryData,
  RiskListExclusionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListExclusionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListExclusionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      riskPolicyId?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListExclusionsQueryData,
): RiskListExclusionsQueryData | undefined {
  const key = queryKeyRiskListExclusions(...queryKeyBase);

  return client.setQueryData<RiskListExclusionsQueryData>(key, data);
}

export function invalidateRiskListExclusions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      riskPolicyId?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "exclusions", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskListExclusions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "exclusions", "list"],
  });
}
